import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Stars } from "@react-three/drei";
import * as THREE from "three";
import { useStore, getEffectiveWeather } from "../store";
import { envState } from "./env";

const skyVert = `
varying vec3 vDir;
void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
`;
const skyFrag = `
varying vec3 vDir;
uniform vec3 top; uniform vec3 horizon; uniform vec3 bottom;
void main(){
  float h = vDir.y;
  vec3 col = h > 0.0 ? mix(horizon, top, pow(clamp(h,0.0,1.0),0.55))
                     : mix(horizon, bottom, clamp(-h*2.0,0.0,1.0));
  gl_FragColor = vec4(col,1.0);
}
`;

const C = (hex) => new THREE.Color(hex);
const daySky = { top: C("#3f7fb0"), horizon: C("#cfe3ef"), bottom: C("#b8c4cc") };
const nightSky = { top: C("#050710"), horizon: C("#0f1830"), bottom: C("#05060c") };
const stormSky = { top: C("#5a636b"), horizon: C("#9aa2a8"), bottom: C("#7d848a") };
const goldenH = C("#f4b26a");

export default function Environment() {
  const dir = useRef();
  const hemi = useRef();
  const amb = useRef();
  const sun = useRef();
  const starGroup = useRef();
  const skyUniforms = useMemo(
    () => ({
      top: { value: daySky.top.clone() },
      horizon: { value: daySky.horizon.clone() },
      bottom: { value: daySky.bottom.clone() },
    }),
    []
  );

  const tmpTop = useMemo(() => new THREE.Color(), []);
  const tmpHor = useMemo(() => new THREE.Color(), []);
  const tmpBot = useMemo(() => new THREE.Color(), []);
  const sunColor = useMemo(() => new THREE.Color(), []);

  useFrame((state) => {
    const s = useStore.getState();
    const t = s.timeOfDay;
    const w = getEffectiveWeather(s);
    const storm = w === "rain" || w === "snow";

    const phi = ((t - 6) / 24) * Math.PI * 2;
    const elev = Math.sin(phi); // -1..1
    const daylight = THREE.MathUtils.clamp(elev, 0, 1);
    const night = THREE.MathUtils.clamp((0.06 - elev) / 0.35, 0, 1);
    envState.night = night;

    // Sky colors
    tmpTop.copy(nightSky.top).lerp(daySky.top, 1 - night);
    tmpHor.copy(nightSky.horizon).lerp(daySky.horizon, 1 - night);
    tmpBot.copy(nightSky.bottom).lerp(daySky.bottom, 1 - night);
    // golden hour tint near horizon
    const golden = THREE.MathUtils.clamp(1 - Math.abs(elev) / 0.25, 0, 1) * (1 - night);
    tmpHor.lerp(goldenH, golden * 0.5);
    if (storm) {
      tmpTop.lerp(stormSky.top, 0.7 * (1 - night));
      tmpHor.lerp(stormSky.horizon, 0.7 * (1 - night));
      tmpBot.lerp(stormSky.bottom, 0.7 * (1 - night));
    }
    skyUniforms.top.value.copy(tmpTop);
    skyUniforms.horizon.value.copy(tmpHor);
    skyUniforms.bottom.value.copy(tmpBot);
    if (state.scene.fog) state.scene.fog.color.copy(tmpHor);

    // Sun direction — pushed to a near-infinite distance so shadows read as parallel.
    const sx = Math.cos(phi) * 6000;
    const sy = Math.sin(phi) * 6000;
    const sz = 2600;
    if (dir.current) {
      dir.current.position.set(sx, Math.max(sy, -50), sz);
      let inten = daylight * 2.2;
      if (storm) inten *= 0.35;
      dir.current.intensity = inten;
      sunColor.copy(C("#fff4e0")).lerp(goldenH, golden);
      dir.current.color.copy(sunColor);
    }
    envState.sunlight = daylight * (storm ? 0.4 : 1);
    if (amb.current) amb.current.intensity = 0.25 + daylight * 0.35 + (storm ? 0.1 : 0);
    if (hemi.current) hemi.current.intensity = 0.3 + daylight * 0.5;
    if (sun.current) {
      sun.current.position.set(sx, sy, sz);
      sun.current.visible = elev > -0.05 && !storm;
      sun.current.material.opacity = daylight;
    }
    if (starGroup.current) starGroup.current.visible = night > 0.15;
  });

  return (
    <group>
      <mesh scale={[3400, 3400, 3400]} frustumCulled={false} renderOrder={0}>
        <sphereGeometry args={[1, 32, 16]} />
        <shaderMaterial
          side={THREE.BackSide}
          depthWrite={false}
          depthTest={false}
          uniforms={skyUniforms}
          vertexShader={skyVert}
          fragmentShader={skyFrag}
        />
      </mesh>

      <mesh ref={sun} scale={[620, 620, 620]}>
        <circleGeometry args={[1, 32]} />
        <meshBasicMaterial color="#fff2cf" transparent depthWrite={false} />
      </mesh>

      <group ref={starGroup}>
        <Stars radius={2600} depth={120} count={4000} factor={40} saturation={0} fade speed={0.4} />
      </group>

      <ambientLight ref={amb} intensity={0.5} />
      <hemisphereLight ref={hemi} color="#eaf2f7" groundColor="#8a8272" intensity={0.6} />
      <directionalLight
        ref={dir}
        castShadow
        intensity={2}
        position={[3000, 5000, 2600]}
        shadow-mapSize={[2048, 2048]}
        shadow-camera-near={1}
        shadow-camera-far={9000}
        shadow-camera-left={-1700}
        shadow-camera-right={1700}
        shadow-camera-top={1700}
        shadow-camera-bottom={-1700}
        shadow-bias={-0.0004}
      />
    </group>
  );
}
